"use client"

import { ChevronDownIcon } from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import type { LandingSwapToken } from "@src/features/landing-swap/types"
import { motion, useReducedMotion } from "framer-motion"
import { useEffect, useState } from "react"
import { trackLandingCta } from "./landingAnalytics"
import { enterTransition, ITEM_STAGGER, ITEMS_DELAY } from "./landingMotion"

const ONE_DAY_MS = 24 * 60 * 60 * 1000

const DEAL_TOKEN_IN: LandingSwapToken = {
  assetId: "nep141:eth-0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48.omft.near",
  symbol: "USDC",
  name: "USD Coin",
  icon: "/static/icons/token/usdc.svg",
  decimals: 6,
  canSwapIn: true,
  canSwapOut: true,
}

const DEAL_TOKEN_OUT: LandingSwapToken = {
  assetId: "nep141:btc.omft.near",
  symbol: "BTC",
  name: "Bitcoin",
  icon: "/static/icons/token/btc.svg",
  decimals: 8,
  canSwapIn: true,
  canSwapOut: true,
}

export function DealsHeader() {
  return (
    <header className="pb-4">
      <h2 className="flex h-9 items-center font-medium text-gray-900 text-xl/7 tracking-tight">
        Private deals
      </h2>
      <p className="max-w-sm text-balance font-medium text-gray-500 text-sm/5">
        Direct, person-to-person trading. No middlemen. No trust needed.
      </p>
    </header>
  )
}

export function DealsContent() {
  const [expiryDate, setExpiryDate] = useState("")
  const reduceMotion = useReducedMotion()
  const animateEnter = !reduceMotion
  const innerDelay = ITEMS_DELAY + ITEM_STAGGER

  useEffect(() => {
    setExpiryDate(formatExpiryDate(new Date(Date.now() + ONE_DAY_MS)))
  }, [])

  return (
    <div className="select-none space-y-4">
      <motion.div
        className="relative"
        {...enterTransition(ITEMS_DELAY, animateEnter)}
      >
        <DealTokenAmountCard
          label="You send"
          token={DEAL_TOKEN_IN}
          amount="60000"
          usdAmount="$60,000.00"
          rounded="top"
        />
        <DealSwitchTokensDivider />
        <DealTokenAmountCard
          label="You receive"
          token={DEAL_TOKEN_OUT}
          amount="1"
          usdAmount="$63,798.04"
          rounded="bottom"
        />
      </motion.div>

      <motion.div
        className="flex items-center justify-between"
        {...enterTransition(innerDelay, animateEnter)}
      >
        <div>
          <div className="font-medium text-base/5 text-gray-900">Expires</div>
          <p className="font-medium text-gray-500 text-sm/4">{expiryDate}</p>
        </div>

        <div className="grid grid-cols-1">
          <div className="-outline-offset-1 col-start-1 row-start-1 rounded-lg bg-white py-1.5 pr-8 pl-3 font-medium text-base text-gray-900 outline-1 outline-gray-200">
            1 day
          </div>
          <ChevronDownIcon className="pointer-events-none col-start-1 row-start-1 mr-2 size-4 self-center justify-self-end text-gray-400" />
        </div>
      </motion.div>

      <motion.div {...enterTransition(innerDelay + ITEM_STAGGER, animateEnter)}>
        <Button
          type="button"
          size="lg"
          className="h-12!"
          href="/login"
          fullWidth
          onClick={() => trackLandingCta("deals_widget")}
        >
          Create account to trade
        </Button>
      </motion.div>
    </div>
  )
}

export default function LandingDealsPreview() {
  return (
    <section>
      <DealsHeader />
      <DealsContent />
    </section>
  )
}

function formatExpiryDate(date: Date) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)
}

function DealTokenAmountCard({
  label,
  token,
  amount,
  usdAmount,
  rounded,
}: {
  label: string
  token: LandingSwapToken
  amount: string
  usdAmount: string
  rounded: "top" | "bottom"
}) {
  return (
    <div
      className={`-outline-offset-1 relative flex w-full flex-col gap-3.5 bg-white p-5 outline outline-gray-200 ${
        rounded === "top" ? "rounded-t-2xl" : "rounded-b-2xl"
      }`}
    >
      <div>
        <div className="pb-3.5 font-medium text-gray-500 text-sm/5">
          {label}
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1 font-medium text-3xl/8 text-gray-900 tracking-tight">
            {amount}
          </div>

          <div className="-outline-offset-1 flex min-w-0 max-w-full shrink-0 items-center gap-1.5 rounded-full bg-gray-50 p-1 pr-1.5 text-left outline-1 outline-gray-900/10">
            <AssetComboIcon
              icon={token.icon}
              sizeClassName="size-7"
              className="shrink-0"
            />
            <div className="font-semibold text-gray-900 text-sm/5 tracking-tight">
              {token.symbol}
            </div>
            <ChevronDownIcon className="size-4 shrink-0 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="flex h-5 items-center justify-start">
        <div className="font-medium text-gray-500 text-sm/5">{usdAmount}</div>
      </div>
    </div>
  )
}

function DealSwitchTokensDivider() {
  return (
    <div className="-my-[20.5px] relative z-3 flex items-center justify-center">
      <div className="-outline-offset-1 flex size-10 items-center justify-center rounded-full bg-white text-gray-400 outline-1 outline-gray-200">
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
          className="size-5"
        >
          <path d="M15 19V6.6586c0-1.0058 0-1.5087.3087-1.6339s.6719.2305 1.3984.9417L19 8.2109M9 5v12.3414c0 1.0058 0 1.5087-.3087 1.6339s-.6719-.2305-1.3984-.9417L5 15.7891" />
        </svg>
      </div>
    </div>
  )
}
